import { MAX_PAGE_SIZE } from '@forecastify/shared';
import { parseList } from './permissions-hook';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 30 Oct 2026 · A LIST THE SCREEN TREATS AS COMPLETE IS READ COMPLETELY.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Every paged endpoint answers 50 rows by default and 200 at most, and the screens read ONE page
 * and then used it as the whole set: a picker of 50 of 87 people, a task list that missed a line
 * item's tasks because they were outside a project's first 200, a project picker that will drop
 * projects past 200 (UAT, 29 Oct). This reads every page — the first, then the rest in parallel
 * from `meta.total` — so a picker, a name lookup or an in-memory filter sees all of it.
 *
 * ⚠️ **For sets that are bounded by the workspace's size** — people, projects, clients, one line
 * item's or one project's tasks, one person's or one week's worklogs. Never for every task in a
 * workspace: that is searched or paged on the server. `ceiling` is the guard against being
 * pointed at one anyway.
 *
 * ⚠️ **All or nothing.** A page that fails makes the whole read fail, which every caller already
 * treats as *could not load*. A partial set is the bug this exists to remove.
 */
type Request = (path: string, init?: RequestInit) => Promise<Response>;
interface Schema<T> {
  safeParse: (value: unknown) => { success: boolean; data?: T };
}

/** The most rows one read fetches before it stops — twenty-five pages. */
export const READ_ALL_CEILING = 5000;

export interface ReadAll<T> {
  readonly rows: T[];
  /** What the server counted for the filtered set — more than `rows.length` past the ceiling. */
  readonly total: number;
}

function pageOf(path: string, offset: number): string {
  const url = new URL(path, 'http://read-all.invalid');
  url.searchParams.set('limit', String(MAX_PAGE_SIZE));
  url.searchParams.set('offset', String(offset));
  /* An offset and a cursor together are served the offset (`page.ts`); dropping it says so. */
  url.searchParams.delete('cursor');
  return `${url.pathname}${url.search}`;
}

interface ListBody {
  data: unknown[];
  meta?: { total?: unknown } & Record<string, unknown>;
}

function listBody(value: unknown): ListBody | null {
  const body = value as ListBody | null;
  return body !== null && typeof body === 'object' && Array.isArray(body.data) ? body : null;
}

function json(body: unknown): Response {
  return new Response(JSON.stringify(body), {
    status: 200,
    headers: { 'content-type': 'application/json' },
  });
}

/**
 * ⚠️ **A drop-in for `session.request(path)` on a list**: one response whose `data` is every page
 * and whose `meta` says there is no next one. A refusal is the first refused page's response, so a
 * caller's `ok` check and its error handling read exactly as before.
 */
export async function requestAll(
  session: { readonly request: Request },
  path: string,
  ceiling: number = READ_ALL_CEILING,
): Promise<Response> {
  const first = await session.request(pageOf(path, 0));
  if (!first.ok) return first;
  const body = listBody(await first.json().catch(() => null));
  /* Not a list after all: hand back what came, re-wrapped because its body has been read. */
  if (body === null) return json(null);
  const total = typeof body.meta?.total === 'number' ? body.meta.total : null;
  /* An unpaged endpoint carries no total: what it sent is everything. */
  if (total === null || total <= body.data.length) return json(body);

  const offsets: number[] = [];
  for (let offset = MAX_PAGE_SIZE; offset < Math.min(total, ceiling); offset += MAX_PAGE_SIZE) {
    offsets.push(offset);
  }
  const rest = await Promise.all(offsets.map((offset) => session.request(pageOf(path, offset))));
  const refused = rest.find((response) => !response.ok);
  if (refused !== undefined) return refused;
  const pages = await Promise.all(rest.map(async (response) => listBody(await response.json())));
  if (pages.some((page) => page === null)) return json(null);

  const data = [...body.data, ...pages.flatMap((page) => page?.data ?? [])];
  return json({
    data,
    meta: { ...body.meta, total, next_cursor: null, offset: 0, limit: data.length },
  });
}

/** Every row of a list, with the server's count. `null` when any page could not be read. */
export async function readAllCounted<T>(
  session: { readonly request: Request },
  path: string,
  schema: Schema<T>,
  ceiling: number = READ_ALL_CEILING,
): Promise<ReadAll<T> | null> {
  const response = await requestAll(session, path, ceiling).catch(() => null);
  if (response?.ok !== true) return null;
  const body: unknown = await response.json().catch(() => null);
  const rows = parseList(schema, body);
  if (rows === null) return null;
  const total = (body as { meta?: { total?: unknown } } | null)?.meta?.total;
  return { rows, total: typeof total === 'number' ? total : rows.length };
}

/** Every row of a list. `null` when any page could not be read. */
export async function readAll<T>(
  session: { readonly request: Request },
  path: string,
  schema: Schema<T>,
  ceiling: number = READ_ALL_CEILING,
): Promise<T[] | null> {
  return (await readAllCounted(session, path, schema, ceiling))?.rows ?? null;
}
